import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Alert,
    Chip,
    Fade,
    Tooltip,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { CURRENCY_TO_COUNTRY } from '../../constants';
import { formatCurrencyRate } from '../../services/rateConverter';
import { useTranslation } from 'react-i18next';

interface CurrencyExchangeFormProps {
    amount: string;
    fromCurrency: string;
    toCurrency: string;
    convertedAmount: number | null;
    exchangeRates: { [key: string]: number };
    lastUpdated: Date | null;
    error: string | null;
    availableCurrencies: string[];
    onAmountChange: (amount: string) => void;
    onFromCurrencyChange: (currency: string) => void;
    onToCurrencyChange: (currency: string) => void;
    onSwapCurrencies: () => void;
    onRefresh?: () => void;
    isRefreshing?: boolean;
    onRateOverride?: (rate: number) => void;
}

const CurrencyExchangeForm: React.FC<CurrencyExchangeFormProps> = ({
    amount,
    fromCurrency,
    toCurrency,
    convertedAmount,
    exchangeRates,
    lastUpdated,
    error,
    availableCurrencies,
    onAmountChange,
    onFromCurrencyChange,
    onToCurrencyChange,
    onSwapCurrencies,
    onRefresh,
    isRefreshing = false,
    onRateOverride,
}) => {
    const { t } = useTranslation();
    const [showResult, setShowResult] = useState(false);
    const [isEditingRate, setIsEditingRate] = useState(false);
    const [customRate, setCustomRate] = useState<string>('');

    // Show result with animation when conversion is available
    useEffect(() => {
        if (convertedAmount !== null && amount && !isNaN(Number(amount))) {
            setShowResult(true);
        } else {
            setShowResult(false);
        }
    }, [convertedAmount, amount]);

    const handleAmountChange = (value: string) => {
        // Only allow numbers and decimal points
        const cleanValue = value.replace(/[^0-9.]/g, '');
        onAmountChange(cleanValue);
    };

    const getCurrentRate = () => {
        if (fromCurrency === toCurrency) return 1;
        return exchangeRates[toCurrency] || 0;
    };

    const formatAmount = (value: number, currency: string) => {
        if (value === null || value === undefined || isNaN(value)) {
            return '0';
        }
        if (currency === 'IRR') {
            return Math.round(value).toLocaleString();
        }
        return value.toFixed(2);
    };

    const handleEditRateClick = () => {
        const currentRate = getCurrentRate();
        setCustomRate(currentRate.toString());
        setIsEditingRate(true);
    };

    const handleSaveRate = () => {
        const rate = parseFloat(customRate);
        if (!isNaN(rate) && rate > 0 && onRateOverride) {
            onRateOverride(rate);
            setIsEditingRate(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditingRate(false);
        setCustomRate('');
    };

    const handleCustomRateChange = (value: string) => {
        // Only allow numbers, decimal points, and commas
        const cleanValue = value.replace(/[^0-9.,]/g, '');
        setCustomRate(cleanValue);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    {t('calculator.title')}
                </Typography>
                {onRefresh && (
                    <Tooltip title={t('common.refresh')}>
                        <IconButton
                            onClick={onRefresh}
                            disabled={isRefreshing}
                            sx={{
                                color: 'primary.main',
                                '&:hover': { backgroundColor: 'primary.light' },
                            }}
                        >
                            <RefreshIcon className={isRefreshing ? 'pulse' : ''} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                    fullWidth
                    label={t('common.amount')}
                    type="text"
                    value={amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    size="medium"
                    placeholder={t('calculator.enterAmount')}
                    sx={{
                        '& .MuiInputBase-input': {
                            fontSize: { xs: '1.1rem', sm: '1rem' },
                            padding: { xs: '16px 14px', sm: '16px 14px' }
                        }
                    }}
                />

                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 2, sm: 2 },
                    alignItems: { xs: 'stretch', sm: 'center' }
                }}>
                    <FormControl fullWidth size="medium">
                        <InputLabel>{t('common.from')}</InputLabel>
                        <Select
                            value={fromCurrency}
                            label={t('common.from')}
                            onChange={(e) => onFromCurrencyChange(e.target.value)}
                            sx={{
                                '& .MuiSelect-select': {
                                    fontSize: { xs: '1rem', sm: '0.875rem' }
                                }
                            }}
                        >
                            {availableCurrencies.map((currency) => {
                                const info = CURRENCY_TO_COUNTRY[currency];
                                return (
                                    <MenuItem key={currency} value={currency}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ fontSize: '1.2rem' }}>{info?.flag || '🌐'}</Box>
                                            <Box sx={{ fontWeight: 500 }}>{currency}</Box>
                                            <Box sx={{
                                                color: 'text.secondary',
                                                fontSize: { xs: '0.8rem', sm: '0.75rem' },
                                                display: { xs: 'none', sm: 'block' }
                                            }}>
                                                ({info?.name || currency})
                                            </Box>
                                        </Box>
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>

                    <IconButton
                        onClick={onSwapCurrencies}
                        sx={{
                            bgcolor: 'primary.main',
                            color: 'white',
                            alignSelf: { xs: 'center', sm: 'center' },
                            width: { xs: '48px', sm: '40px' },
                            height: { xs: '48px', sm: '40px' },
                            '&:hover': {
                                bgcolor: 'primary.dark',
                            }
                        }}
                    >
                        <SwapHorizIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.25rem' } }} />
                    </IconButton>

                    <FormControl fullWidth size="medium">
                        <InputLabel>{t('common.to')}</InputLabel>
                        <Select
                            value={toCurrency}
                            label={t('common.to')}
                            onChange={(e) => onToCurrencyChange(e.target.value)}
                            sx={{
                                '& .MuiSelect-select': {
                                    fontSize: { xs: '1rem', sm: '0.875rem' }
                                }
                            }}
                        >
                            {availableCurrencies.map((currency) => {
                                const info = CURRENCY_TO_COUNTRY[currency];
                                return (
                                    <MenuItem key={currency} value={currency}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ fontSize: '1.2rem' }}>{info?.flag || '🌐'}</Box>
                                            <Box sx={{ fontWeight: 500 }}>{currency}</Box>
                                            <Box sx={{
                                                color: 'text.secondary',
                                                fontSize: { xs: '0.8rem', sm: '0.75rem' },
                                                display: { xs: 'none', sm: 'block' }
                                            }}>
                                                ({info?.name || currency})
                                            </Box>
                                        </Box>
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}

            <Fade in={showResult}>
                <Box sx={{
                    mt: 3,
                    textAlign: 'center',
                    p: { xs: 2, sm: 3 },
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 2,
                }}>
                    <Typography variant="h4" sx={{
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        fontWeight: 700,
                        mb: 1,
                        color: 'primary.main'
                    }}>
                        <Box component="span" sx={{ 
                            direction: 'ltr', 
                            display: 'inline-block',
                            fontFamily: 'monospace'
                        }}>
                            {amount || '0'} {fromCurrency} = {convertedAmount !== null ? formatAmount(convertedAmount, toCurrency) : '0'} {toCurrency}
                        </Box>
                    </Typography>

                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {!isEditingRate ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <Typography variant="body1" color="text.secondary">
                                    {t('calculator.currentRate')}: 
                                    <Box component="span" sx={{ 
                                        direction: 'ltr', 
                                        display: 'inline-block',
                                        fontFamily: 'monospace',
                                        ml: 0.5,
                                        mr: 0.5
                                    }}>
                                        1 {fromCurrency} = {formatCurrencyRate(getCurrentRate(), toCurrency)} {toCurrency}
                                    </Box>
                                </Typography>
                                <Tooltip title={t('calculator.editRate')}>
                                    <IconButton
                                        size="small"
                                        onClick={handleEditRateClick}
                                        sx={{
                                            color: 'primary.main',
                                            '&:hover': { bgcolor: 'primary.light', color: 'white' }
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                    1 {fromCurrency} =
                                </Typography>
                                <TextField
                                    size="small"
                                    value={customRate}
                                    onChange={(e) => handleCustomRateChange(e.target.value)}
                                    placeholder="Enter rate"
                                    sx={{
                                        width: '150px',
                                        '& .MuiInputBase-input': {
                                            fontSize: '0.875rem',
                                            padding: '4px 8px'
                                        }
                                    }}
                                    autoFocus
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSaveRate();
                                        } else if (e.key === 'Escape') {
                                            handleCancelEdit();
                                        }
                                    }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    {toCurrency}
                                </Typography>
                                <Tooltip title={t('common.save')}>
                                    <IconButton
                                        size="small"
                                        onClick={handleSaveRate}
                                        sx={{
                                            color: 'success.main',
                                            '&:hover': { bgcolor: 'success.light', color: 'white' }
                                        }}
                                    >
                                        <CheckIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t('common.cancel')}>
                                    <IconButton
                                        size="small"
                                        onClick={handleCancelEdit}
                                        sx={{
                                            color: 'error.main',
                                            '&:hover': { bgcolor: 'error.light', color: 'white' }
                                        }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}

                        {lastUpdated && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                                <Chip
                                    label={`${t('calculator.lastUpdated')}: ${new Date(lastUpdated).toLocaleTimeString()}`}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                />
                            </Box>
                        )}
                    </Box>
                </Box>
            </Fade>
        </Box>
    );
};

export default CurrencyExchangeForm; 